// utils/cardScopes.js
//
// Card-definition ownership scopes (mirrors keepr-api docs/SCHEMA.md):
//   global      keepr-controlled OOB cards — readable by everyone, editable by
//               admins only, extensible (a collection card may extend one).
//   collection  owned by exactly one collection (collection_id) — managed by
//               that collection's managers, never usable by another collection.
//   user        TRANSITIONAL: belonged to no collection at migration time;
//               private to its creator until the Card Library assigns it.
//   (legacy)    docs with no scope yet (pre-migration window) behave like the
//               old owner model; treat as assignable like global for UI.

import { normalizeId } from './ids';

export function getCardScope(card) {
	if (!card) return null;
	if (card.scope === 'global' || card.scope === 'collection' || card.scope === 'user') return card.scope;
	return 'legacy';
}

export function isOwnedByCollection(card, collectionId) {
	if (getCardScope(card) !== 'collection') return false;
	const owner = normalizeId(card?.collection_id);
	const target = normalizeId(collectionId);
	// Both ids must exist: an orphaned collection card (missing collection_id)
	// checked against an unsaved collection (no _id yet) must not match on
	// undefined === undefined.
	return !!owner && !!target && owner === target;
}

/**
 * May this card become a MEMBER of the given collection (an embedded
 * collection.cards[] ref)? Global and legacy cards: yes. A collection-owned
 * card only of its own collection (it's an automatic member there anyway).
 * Transitional cards can't be used until assigned (the assignment flow is a
 * future Card Library action — the API rejects them for item creation).
 */
export function canAddToCollection(card, collectionId) {
	const scope = getCardScope(card);
	if (scope === 'global' || scope === 'legacy') return true;
	if (scope === 'collection') return isOwnedByCollection(card, collectionId);
	return false;
}

/**
 * Short badge label for a card in the context of one collection.
 * null = no badge worth showing (legacy docs during the migration window).
 */
export function cardScopeLabel(card, collectionId = null) {
	const scope = getCardScope(card);
	if (scope === 'global') return 'Global';
	if (scope === 'user') return 'Unassigned';
	if (scope === 'collection') {
		if (collectionId == null) return 'Collection card';
		return isOwnedByCollection(card, collectionId) ? 'This collection' : 'Other collection';
	}
	return null;
}

/**
 * A RETIRED global card (keepr-api KPR-280, the owner's ruling G14:
 * "retiring replaces deleting" a global card in use). It has left the Card
 * Library — no collection may add it, no new card may extend it — while every
 * collection that already holds it keeps working. The API sets `retiredAt`
 * only on a global card; anything else is never read as retired.
 */
export function isRetiredCard(card) {
	return getCardScope(card) === 'global' && Boolean(card?.retiredAt);
}

/**
 * The API's `usage.inUse` (admins only, global cards only — keepr-api
 * SCHEMA.md "A global card in use only grows") as a clause a sentence can
 * carry: "2 collections hold it, 1 card extends it, it has items". Null when
 * the card is not in use, or when the API did not say (an older API, a reader
 * who is not an admin) — the caller then behaves as before.
 */
export function inUseWords(inUse) {
	if (!inUse || inUse.inUse !== true) return null;
	const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
	const parts = [];
	if (inUse.collections > 0) parts.push(`${plural(inUse.collections, 'collection', 'collections')} ${inUse.collections === 1 ? 'holds' : 'hold'} it`);
	if (inUse.extendedBy > 0) parts.push(`${plural(inUse.extendedBy, 'card', 'cards')} ${inUse.extendedBy === 1 ? 'extends' : 'extend'} it`);
	if (inUse.items) parts.push('it has items');
	const deleted = inUse.inRecentlyDeleted > 0 ? ` (${inUse.inRecentlyDeleted} in Recently deleted)` : '';
	return parts.length ? `${parts.join(', ')}${deleted}` : 'it is in use';
}

/**
 * The API's `usage.lookedUp` (a manager or an admin only; a card of a
 * published or retiring collection — keepr-api KPR-281, SCHEMA.md "A
 * published collection's cards that others look up only grow") as the clause
 * after the card's name: "is looked up by 2 other collections while this
 * collection is published". `via` says where the looked-up card sits in this
 * card's line — the card itself, a card extending it, or a card it extends —
 * so the sentence says why THIS card is held. Null when nothing looks it up,
 * or when the API did not say (an older API, a reader below manage) — the
 * caller then behaves as before. Counts only: the API never names the other
 * collections.
 */
export function lookedUpClause(lookedUp) {
	if (!lookedUp || lookedUp.lookedUp !== true) return null;
	const n = Number(lookedUp.collections) || 0;
	const who = n > 0 ? `${n} other ${n === 1 ? 'collection' : 'collections'}` : 'other collections';
	const verb = n === 1 ? 'looks' : 'look';
	const state = lookedUp.state === 'retiring' ? 'retiring' : 'published';
	const when = `while this collection is ${state}`;
	if (lookedUp.via === 'descendant') return `is extended by a card ${who} ${verb} up ${when}`;
	if (lookedUp.via === 'ancestor') return `extends a card ${who} ${verb} up ${when}`;
	return `is looked up by ${who} ${when}`;
}
